// Lightweight, GDPR-friendly conversion tracking. Backend is Plausible
// (cookieless, EU-hosted); without the env vars it degrades to an in-memory
// queue so no personal data or cookies are ever stored on the device.
//
// Env: NEXT_PUBLIC_PLAUSIBLE_DOMAIN, optional NEXT_PUBLIC_PLAUSIBLE_HOST,
// NEXT_PUBLIC_ANALYTICS_DEBUG=1 for console output.

import { getStatsConsent } from "@/lib/consent";

type PlausibleFn = (event: string, opts?: { props?: Record<string, string | number | boolean> }) => void;

declare global {
  interface Window {
    plausible?: PlausibleFn;
    __dw_events?: Array<{ ts: number; event: string; props?: Record<string, unknown> }>;
  }
}

export type TrackProps = Record<string, string | number | boolean | null | undefined>;

function clean(props?: TrackProps): Record<string, string | number | boolean> | undefined {
  if (!props) return undefined;
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(props)) {
    if (v === null || v === undefined) continue;
    out[k] = v;
  }
  return Object.keys(out).length ? out : undefined;
}

export function track(event: string, props?: TrackProps): void {
  if (typeof window === "undefined") return;
  const cleaned = clean(props);

  try {
    window.__dw_events = window.__dw_events || [];
    window.__dw_events.push({ ts: Date.now(), event, props: cleaned });
    if (window.__dw_events.length > 200) window.__dw_events.shift();
  } catch {
    // no-op
  }

  if (typeof window.plausible === "function") {
    try {
      window.plausible(event, cleaned ? { props: cleaned } : undefined);
    } catch {
      // no-op
    }
  }

  // Google Analytics, only with statistics consent: the Google tag exists
  // then (components/GoogleAdsTag), never before (11.10.2026).
  if (typeof window.gtag === "function" && getStatsConsent() === "granted") {
    try {
      window.gtag("event", event, cleaned ?? {});
    } catch {
      // no-op
    }
  }

  if (process.env.NEXT_PUBLIC_ANALYTICS_DEBUG === "1") {
    console.info("[track]", event, cleaned ?? "");
  }
}

export const EVENT = {
  CTA_HERO_CHECK: "cta_hero_check",
  CTA_HERO_ANFRAGE: "cta_hero_anfrage",
  CTA_HERO_PACKAGES: "cta_hero_packages",
  CTA_HERO_WHATSAPP: "cta_hero_whatsapp",
  CTA_HEADER_ANFRAGE: "cta_header_anfrage",
  CTA_HEADER_PACKAGES: "cta_header_packages",
  CTA_PRICING_BOOK: "cta_pricing_book",
  CTA_FINAL_PRIMARY: "cta_final_primary",
  CTA_STICKY_MINI: "cta_sticky_mini",
  CTA_WA_PREVIEW: "cta_whatsapp_preview",
  CTA_WA_LIVE_BUBBLE: "cta_whatsapp_live_bubble",
  CTA_WA_LIVE_FAB: "cta_whatsapp_live_fab",
  CTA_WA_FAB: "cta_whatsapp_fab",
  FAQ_EXPAND: "faq_expand",
  QUIZ_START: "quiz_start",
  QUIZ_COMPLETE: "quiz_complete",
  EXIT_INTENT_SHOWN: "exit_intent_shown",
  EXIT_INTENT_SUBMIT: "exit_intent_submit",
  LEAD_MAGNET_SUBMIT: "lead_magnet_submit",
  LOCALE_CHANGE: "locale_change",
  FORM_INQUIRY_SUBMIT: "form_inquiry_submit",
  FORM_ELIGIBILITY_COMPLETE: "form_eligibility_complete",
  
  FUNNEL_HERO_VIEW: "funnel_hero_view",
  FUNNEL_TRUST_VIEW: "funnel_trust_view",
  FUNNEL_PROCESS_VIEW: "funnel_process_view",
  FUNNEL_PRICING_VIEW: "funnel_pricing_view",
  FUNNEL_FAQ_VIEW: "funnel_faq_view",
  FUNNEL_CHECKOUT_START: "funnel_checkout_start",
  FUNNEL_CHECKOUT_COMPLETE: "funnel_checkout_complete",
  
  SCROLL_DEPTH_25: "scroll_depth_25",
  SCROLL_DEPTH_50: "scroll_depth_50",
  SCROLL_DEPTH_75: "scroll_depth_75",
  SCROLL_DEPTH_100: "scroll_depth_100",
  
  TIME_ON_PAGE_30S: "time_on_page_30s",
  TIME_ON_PAGE_60S: "time_on_page_60s",
  TIME_ON_PAGE_120S: "time_on_page_120s",
  TIME_ON_PAGE_300S: "time_on_page_300s",
  
  VIDEO_PLAY: "video_play",
  VIDEO_COMPLETE: "video_complete",
  TESTIMONIAL_VIEW: "testimonial_view",
  GALLERY_VIEW: "gallery_view",
  PACKAGE_COMPARE: "package_compare",
  TRUST_BADGE_CLICK: "trust_badge_click",
} as const;
